<template>
  <v-flex>
    <v-card-text>
      <v-progress-linear
        v-if="!company"
        height="2"
        indeterminate/>
      <v-form
        v-else
        v-model="isFormValid"
        autocomplete="off"
        @keyup.native.enter="saveCompany">
        <v-subheader class="subheader-margin-left">{{ $t('basicData') }}</v-subheader>
        <v-text-field
          v-model="companyEditorData.name"
          :error-messages="errorMessages.name"
          :label="$t('name') + '*'"
          :rules="[$rules.isRequired]"
          class="mx-3"
        />

        <v-text-field
          v-model="companyEditorData.vat_number"
          :error-messages="errorMessages.vat_number"
          :label="$t('vatNumber')"
          class="mx-3"/>

        <v-subheader class="subheader-margin-left">{{ $t('address') }}</v-subheader>

        <v-text-field
          v-model="companyEditorData.street"
          :error-messages="errorMessages.street"
          :label="$t('street') + '*'"
          :rules="[$rules.isRequired]"
          class="mx-3"
        />

        <select-city
          :error-messages="errorMessages.city || errorMessages.city_id"
          :value="companyEditorData.city"
          class="mx-3 m-3"
          @input="cityInput"/>

        <v-subheader class="subheader-margin-left">{{ $t('contact') }}</v-subheader>

        <v-text-field
          v-model="companyEditorData.phone"
          :error-messages="errorMessages.phone"
          :label="$t('phone') + '*'"
          :rules="[$rules.isRequired]"
          class="mx-3"
        />

        <v-text-field
          v-model="companyEditorData.fax"
          :error-messages="errorMessages.fax"
          :label="$t('fax')"
          class="mx-3"/>

        <v-text-field
          v-model="companyEditorData.email"
          :error-messages="errorMessages.email"
          :label="$t('email')"
          :rules="[$rules.isValidEmail]"
          class="mx-3"
        />

        <v-text-field
          v-model="companyEditorData.contact_person"
          :error-messages="errorMessages.contact_person"
          :label="$t('contactPerson')"
          class="mx-3"
        />

        <v-subheader class="subheader-margin-left">{{ $t('timesheetSettings') }}</v-subheader>
        <v-checkbox
          v-model="companyEditorData.using_cost_units"
          :label="$t('usingCostUnits')"
          :error-messages="errorMessages.using_cost_units"
          :value="0"
          :checked="1"
          color="success"
          class="mx-3"/>

        <v-checkbox
          v-model="companyEditorData.show_plan_hours_calculation_paragraph"
          :label="$t('showPlanHoursParagraph')"
          class="mx-3"/>

        <v-select
          :label="$t('allocationBlockSize')"
          v-model="companyEditorData.timesheet_allocation_block_size"
          :items="allocationBlockSize"
          class="mx-3"
        />

      </v-form>

    </v-card-text>
    <v-card-actions>
      <v-spacer/>

      <disabled-user-button>
        <v-btn
          slot-scope="{isDisabledForUser}"
          :loading="isLoading"
          :disabled="isDisabledForUser || isButtonDisabled"
          color="primary"
          @click="saveCompany()"
        >
          {{ $t('save') }}
        </v-btn>
      </disabled-user-button>

    </v-card-actions>
  </v-flex>
</template>

<script>
import SelectCity from '@/components/global-components/inputs/SelectCity'
import EmailInput from '@/components/global-components/inputs/EmailInput'
import DisabledUserButton from '@/components/global-components/disabledUserButton/disabledUserButton'

export default {
  name: 'CompanyEditor',
  components: { DisabledUserButton, EmailInput, SelectCity },
  props: ['company', 'errorMessages', 'isLoading'],
  data () {
    return {
      companyEditorData: {},
      isFormValid: true
    }
  },
  computed: {
    isButtonDisabled () {
      return this.isLoading || // Sending data to backend
              !this.isFormValid || // If form not valid
              !this.company // If company fetching or empty
    },
    allocationBlockSize () {
      return [
        { text: '15 min', value: 15 },
        { text: '30 min', value: 30 },
        { text: '60 min', value: 60 }
      ]
    }
  },
  methods: {
    saveCompany () {
      if (this.isFormValid) {
        this.$emit('saveCompany', this.companyEditorData)
      }
    },
    cityInput (city) {
      if (city) {
        this.companyEditorData.city = city
        this.companyEditorData.city_id = city.id
      } else {
        this.companyEditorData.city = null
        this.companyEditorData.city_id = null
      }
    }
  },
  i18n: {
    messages: {
      en: {
        address: 'Address data',
        basicData: 'Basic data',
        contact: 'Contact data',
        contactPerson: 'Contact person',
        email: 'E-Mail',
        fax: 'Fax',
        isRequired: 'Required field',
        name: 'Company name',
        phone: 'Phone',
        save: 'Save',
        street: 'Street, House',
        vatNumber: 'Tax Number (USt-ID)',
        timesheetSettings: 'Timesheet settings',
        usingCostUnits: 'Cost centers on timesheets',
        showPlanHoursParagraph: 'Show plan-based calculation paragraph',
        allocationBlockSize: 'Smallest unit for timesheet calculation'
      },
      tr: {
        address: 'Adres bilgileri',
        basicData: 'Genel bilgiler',
        contact: 'İletişim bilgileri',
        contactPerson: 'İletişim kişisi',
        email: 'E-posta',
        fax: 'Faks',
        isRequired: 'Gerekli alan',
        name: 'Şirket adı',
        phone: 'Telefon',
        save: 'Kaydet',
        street: 'Sokak, Bina',
        vatNumber: 'Vergi numarası (USt-ID)',
        timesheetSettings: 'Zaman çizelgesi ayarları',
        usingCostUnits: 'Zaman çizelgesindeki harcama merkezleri',
        showPlanHoursParagraph: 'Plana dayalı hesaplama paragrafını göster',
        allocationBlockSize: 'Hesaplama için en küçük birim'
      },
      de: {
        address: 'Adressdaten',
        basicData: 'Basisdaten',
        contact: 'Kontaktdaten',
        contactPerson: 'Kontaktperson',
        email: 'E-Mail',
        fax: 'Fax',
        isRequired: 'Benötigt',
        name: 'Firmenname',
        phone: 'Telefon',
        save: 'Speichern',
        street: 'Straße, Hausnummer',
        vatNumber: 'USt-ID Nr.',
        timesheetSettings: 'Einstellungen',
        usingCostUnits: 'Kostenstellen auf Stundenzettel',
        showPlanHoursParagraph: 'Paragraph zur Planstunden-Berechnung anzeigen',
        allocationBlockSize: 'Kleinste Zeiteinheit für Stundenzettel'
      },
      ru: {
        address: 'Адресс',
        basicData: 'Базовые данные',
        contact: 'Контактные данные',
        contactPerson: 'Контактное лицо',
        email: 'Эл. адрес',
        fax: 'Fax',
        isRequired: 'Обязательное поле',
        name: 'Название компании',
        phone: 'Телефон',
        save: 'Сохранить',
        street: 'Улица, Дом',
        vatNumber: 'Налоговый номер (USt-ID)',
        timesheetSettings: 'Настройки табеля',
        usingCostUnits: 'Цены затрат на табелях',
        showPlanHoursParagraph: 'Наименьшая единица времени для расписаний',
        allocationBlockSize: 'Наименьшая единица для расчета расписания'
      }
    }
  },
  watch: {
    company: {
      deep: true,
      handler () { this.companyEditorData = Object.assign({}, this.company) }
    },
    companyEditorData: {
      deep: true,
      handler () { this.$emit('clearErrors') }
    }
  }
}
</script>

<style scoped>
  .subheader-margin-left {
    margin-left: -1em;
  }
</style>
